<template>
    <div class="callback-container">
        <div v-if="isRegistering" class="registration-message">
            <h2>Connexion réussie !</h2>
            <p>Vous êtes maintenant connecté avec Discord.</p>
            <p>Vous pouvez fermer cette fenêtre et continuer votre inscription.</p>
            <button @click="closeWindow" class="close-button">Fermer cette fenêtre</button>
        </div>
        <div v-else>
            <p>Authentification en cours...</p>
        </div>
    </div>
</template>

<script>
import { authService } from '../services/auth';
import { subscriptionService } from '../services/subscription';
import { impersonationStore } from '../store/impersonation';
import { notifications } from '../store/notifications';

export default {
    name: 'Callback',
    data() {
        return {
            isRegistering: false
        };
    },
    methods: {
        closeWindow() {
            window.close();
        },
        clearAuthSession() {
            localStorage.removeItem('discord_token');
            localStorage.removeItem('discord_user');
            localStorage.removeItem('subscription_info');
            localStorage.removeItem('is_admin');
            localStorage.removeItem('impersonated_user');
        },
        async tryModeratorDashboardFallback() {
            const moderatorResponse = await subscriptionService.getModeratorSubscriptions();
            const moderatedSubscriptions = moderatorResponse?.data?.data || [];

            if (!Array.isArray(moderatedSubscriptions) || moderatedSubscriptions.length === 0) {
                return false;
            }

            const targetSubscription = moderatedSubscriptions[0];
            await impersonationStore.setCurrentUser({
                id: targetSubscription.id,
                twitchName: targetSubscription.twitchName
            });

            notifications.add({
                type: 'success',
                message: `Connexion modérateur réussie sur ${targetSubscription.twitchName}.`,
                timeout: 5000
            });

            if (moderatedSubscriptions.length > 1) {
                notifications.add({
                    type: 'info',
                    message: 'Plusieurs souscriptions modérées détectées. Vous pourrez changer de chaîne dans "Mode modérateur".',
                    timeout: 7000
                });
            }

            this.$router.push('/dashboard');
            return true;
        }
    },
    async mounted() {
        try {
            // Vérifier si l'utilisateur est en cours d'inscription
            this.isRegistering = localStorage.getItem('is_registering') === 'true';
            
            const hash = window.location.hash.substring(1);
            const params = new URLSearchParams(hash);
            const accessToken = params.get('access_token');

            if (!accessToken) {
                throw new Error('No access token found');
            }

            // Store token securely
            localStorage.setItem('discord_token', accessToken);

            try {
                // Get user data
                const userData = await authService.getUserInfo();
                localStorage.setItem('discord_user', JSON.stringify(userData));

                // Get subscription data
                const subscriptionData = await authService.getSubscriptionInfo({ useCache: false });
                if (!subscriptionData || subscriptionData.success !== true) {
                    throw new Error('No Buddi account found for this Discord user');
                }
                // On stocke directement l'objet, pas besoin de le parser à nouveau
                localStorage.setItem('subscription_info', JSON.stringify(subscriptionData));

                // Show success notification
                notifications.add({
                    type: 'success',
                    message: 'Connexion réussie !',
                    timeout: 3000
                });

                // Si l'utilisateur est en cours d'inscription, ne pas rediriger
                // Il fermera manuellement la fenêtre et continuera son inscription
                if (!this.isRegistering) {
                    impersonationStore.clearCurrentUser();
                    // Rediriger vers le dashboard seulement si l'utilisateur n'est pas en cours d'inscription
                    this.$router.push('/dashboard');
                }
                // Sinon, afficher le message de succès et attendre que l'utilisateur ferme la fenêtre
                // ou utilise le bouton pour la fermer
            } catch (error) {
                // L'erreur a déjà été gérée par l'intercepteur API
                if (!this.isRegistering) {
                    const hasModeratorAccess = await this.tryModeratorDashboardFallback();
                    if (hasModeratorAccess) {
                        return;
                    }

                    notifications.add({
                        type: 'info',
                        message: 'Aucun compte BuddiBot trouvé. Cliquez sur "Créer un compte" pour terminer votre inscription.',
                        timeout: 7000
                    });
                    this.clearAuthSession();
                    this.$router.push('/');
                }
            }
        } catch (error) {
            console.error('Authentication error:', error);
            notifications.add({
                type: 'error',
                message: 'Erreur lors de l\'authentification. Veuillez réessayer.',
                timeout: 5000
            });
            if (!this.isRegistering) {
                this.clearAuthSession();
                this.$router.push('/');
            }
        }
    }
}
</script>

<style scoped>
.callback-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: linear-gradient(135deg, #015a66 0%, #016e7e 100%);
    color: white;
    padding: 20px;
}

.registration-message {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 30px;
    text-align: center;
    max-width: 500px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.registration-message h2 {
    color: #eda526;
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 1.8rem;
}

.registration-message p {
    margin-bottom: 15px;
    font-size: 1.1rem;
    line-height: 1.5;
}
</style>